import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { fetchApi } from "../services/api";

type Props = {
  email: string;
  onClose: () => void;
}

type MailerBounce = {
  Created: number;
  Email: string;
  Reason: string;
  Status: string;
}

const EmailBounceDialog = ({ email, onClose }: Props) => {
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [bounces, setBounces] = useState<MailerBounce[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadBounceStatus = () => {
    setLoading(true);
    setError(null);
    fetchApi(`/api/Email/BounceStatus?email=${encodeURIComponent(email)}`)
      .then((data: MailerBounce[]) => {
        setBounces(data || []);
      })
      .catch(() => {
        setError('Unable to reach SendGrid right now. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const removeBounce = () => {
    setClearing(true);
    setError(null);
    fetchApi(`/api/Email/RemoveBounce?email=${encodeURIComponent(email)}`, {}, 'delete')
      .then(() => {
        setMessage('Removed the SendGrid bounce block for this address.');
        loadBounceStatus();
      })
      .catch(() => {
        setError('Unable to remove the SendGrid bounce block. Please try again.');
      })
      .finally(() => {
        setClearing(false);
      });
  };

  useEffect(() => {
    setMessage(null);
    loadBounceStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const hasBounce = bounces.length > 0;

  return <Dialog title="Email Delivery Check" onClose={onClose} className="dialog-w800">
    {loading && <div className="text-center py-3">
      <Loader size="medium" />
    </div>}
    {!loading && <>
      {error && <div className="alert alert-danger">
        {error}
        <div className="mt-2">
          <Button size="small" onClick={loadBounceStatus}>Retry</Button>
        </div>
      </div>}
      {!error && <>
        {message && <div className="alert alert-info">{message}</div>}
        {hasBounce
          ? <div className="alert alert-warning">SendGrid is currently blocking {email}. Remove the block so future messages can be delivered.</div>
          : <div className="alert alert-success">{email} is not currently blocked by SendGrid.</div>
        }
        {hasBounce && <div className="list-group mb-3">
          {bounces.map((bounce, idx) => (
            <div className="list-group-item" key={idx}>
              <div className="small text-muted">
                {dayjs.unix(bounce.Created).format('MM/DD/YYYY h:mm A')} {bounce.Status && `(${bounce.Status})`}
              </div>
              <div className="font-weight-bold">{bounce.Reason || 'No reason provided.'}</div>
            </div>
          ))}
        </div>}
      </>}
    </>}
    <DialogActionsBar>
      <Button onClick={onClose}>Close</Button>
      {hasBounce && <Button
        themeColor="primary"
        disabled={clearing || loading || !email}
        onClick={removeBounce}
      >
        {clearing ? <Loader size="small" /> : 'Remove Bounce Block'}
      </Button>}
    </DialogActionsBar>
  </Dialog>;
}

export default EmailBounceDialog;
